import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Image,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useEffect } from 'react';

import { AppShell } from '../../components/app-shell/AppShell';
import {
  AvailabilityCalendar,
  type CalendarEntry,
} from '../../components/calendar/AvailabilityCalendar';
import { MarketingShell } from '../../components/marketing/MarketingShell';
import {
  type ArtistMilestones,
  MilestonesBlock,
} from '../../components/profile/MilestonesBlock';
import { ReportButton } from '../../components/ReportButton';
import { type ReviewCardData } from '../../components/reviews/ReviewCard';
import { ReviewsSection } from '../../components/reviews/ReviewsSection';
import { EmptyState } from '../../components/ui/EmptyState';
import { PageFrame } from '../../components/ui/PageFrame';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { fetchOk } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { track } from '../../lib/telemetry';

type AggregateBucket = { avg: number; count: number };
type AggregateScores = {
  communication?: AggregateBucket;
  hospitality?: AggregateBucket;
  performance?: AggregateBucket;
  safety?: AggregateBucket;
  exchangeRespected?: AggregateBucket;
};

type TouringHistoryEntry = {
  exchangeId: string;
  venueName: string;
  venueSlug: string;
  venueCity: string;
  endDate: string;
  partySize: number;
};

type ArtistDetailDTO = {
  id: string;
  slug: string;
  displayName: string;
  bio: string | null;
  city: string | null;
  country: string | null;
  disciplineSummary: string | null;
  links: { kind: string; url: string; label?: string }[];
  profilePhotoUrl: string | null;
  galleryImageUrls: string[];
  artForms: { slug: string; name: string; group: string }[];
  verified: boolean;
  reviewSummary: {
    visible: boolean;
    completedExchanges: number;
    threshold: number;
    aggregateReviewScores: AggregateScores | null;
    reviewCount: number;
  };
  touringHistory: TouringHistoryEntry[];
  /** T4 — released reviews + per-review response surface. */
  reviews: ReviewCardData[];
  reviews_total: number;
  /** T4 — artist user id, used to decide if the viewer is the subject. */
  reviewee_user_id: string;
};

async function fetchArtist(slug: string): Promise<ArtistDetailDTO | null> {
  const res = await fetch(`/api/v/artists/${encodeURIComponent(slug)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error('Couldn\'t load this artist.');
  return (await res.json()) as ArtistDetailDTO;
}

/**
 * Wave A — milestones fetch is independent of the main detail fetch so a
 * milestones failure doesn't block the page. Returns null on any error
 * (404, network, malformed) — the block renders nothing in that state.
 */
async function fetchArtistMilestones(slug: string): Promise<ArtistMilestones | null> {
  try {
    const res = await fetchOk(`/api/v/artists/${encodeURIComponent(slug)}/milestones`);
    return (await res.json()) as ArtistMilestones;
  } catch {
    return null;
  }
}

export const Route = createFileRoute('/artists/$slug')({
  component: ArtistDetailPage,
});

// FR-144: stable, module-scope full-width AppShell wrapper. Defining it inline
// in the render body would mint a new component type each render and remount
// the subtree. tone="full" so the inner PageFrame owns width identically across
// both shells (MarketingShell has no width opinion).
function AppShellFull({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <AppShell tone="full">{children}</AppShell>;
}

function ArtistDetailPage(): React.JSX.Element {
  const { slug } = Route.useParams();
  const { user } = useAuth();
  const query = useQuery({
    queryKey: ['artist', slug],
    queryFn: () => fetchArtist(slug),
  });
  const milestonesQuery = useQuery({
    queryKey: ['artist', slug, 'milestones'],
    queryFn: () => fetchArtistMilestones(slug),
  });

  let body: React.JSX.Element;
  if (query.isLoading) {
    body = (
      <Stack gap="md">
        <Skeleton h={240} />
        <Skeleton h={32} w="50%" />
        <Skeleton h={16} w="30%" />
        <Skeleton h={120} />
      </Stack>
    );
  } else if (query.isError) {
    body = (
      <Paper p="xl" withBorder>
        <Stack gap="xs" align="center">
          <Title order={3}>This profile couldn&apos;t load right now.</Title>
          <Button variant="default" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </Stack>
      </Paper>
    );
  } else if (!query.data) {
    body = <NotFound />;
  } else {
    // Milestones are best-effort: while loading we pass null (block renders
    // nothing rather than zero-state placeholders). Once it resolves the
    // block fades in beneath the hero.
    body = (
      <ArtistDetail
        artist={query.data}
        milestones={milestonesQuery.data ?? null}
      />
    );
  }
  // FR-144: anonymous visitors — the primary audience for a shared/indexed
  // artist link — get marketing chrome with sign-up CTAs, not the signed-in
  // productivity shell whose Dashboard/Inbox links bounce to sign-in.
  const Shell = user === null ? MarketingShell : AppShellFull;
  return (
    <Shell>
      <PageFrame tone="content">{body}</PageFrame>
    </Shell>
  );
}

function ArtistDetail({
  artist,
  milestones,
}: {
  artist: ArtistDetailDTO;
  milestones: ArtistMilestones | null;
}): React.JSX.Element {
  const { user } = useAuth();
  const place = [artist.city, artist.country].filter(Boolean).join(', ');
  useEffect(() => {
    track('artist.detail_viewed', {
      verified: artist.verified,
      hasTouringHistory: artist.touringHistory.length > 0,
    });
  }, [artist.id, artist.verified, artist.touringHistory.length]);
  return (
    <Stack gap="xl">
      {/* §6 — back affordance; the public artist profile is reached from browse
          and from a host's exchange view. */}
      <Anchor
        component={Link}
        to="/browse/artists"
        size="sm"
        c="ink.6"
        style={{ width: 'fit-content' }}
      >
        ← Browse artists
      </Anchor>
      <Grid gap="lg">
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {artist.profilePhotoUrl ? (
              <Image
                src={artist.profilePhotoUrl}
                alt={artist.displayName}
                fit="cover"
                radius="md"
                h={240}
              />
            ) : (
              <Paper bg="gray.1" h={240} radius="md" />
            )}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Stack gap="xs">
              <Group gap="sm" wrap="wrap" justify="space-between" align="flex-start">
                <Group gap="xs" wrap="wrap">
                  <Title order={1}>{artist.displayName}</Title>
                  {artist.verified && (
                    <Badge variant="light" color="tide">
                      Verified
                    </Badge>
                  )}
                </Group>
                <ReportButton
                  subjectType="artist"
                  subjectId={artist.id}
                  subjectLabel={artist.displayName}
                  variant="button"
                />
              </Group>
              {(artist.disciplineSummary || place) && (
                <Text c="dimmed">
                  {[artist.disciplineSummary, place].filter(Boolean).join(' · ')}
                </Text>
              )}
              {artist.reviewSummary.visible && artist.reviewSummary.aggregateReviewScores && (
                <Text size="sm" c="dimmed">
                  ★ {weightedAverage(artist.reviewSummary.aggregateReviewScores).toFixed(1)} (
                  {artist.reviewSummary.reviewCount} review
                  {artist.reviewSummary.reviewCount === 1 ? '' : 's'})
                </Text>
              )}
              {artist.artForms.length > 0 && (
                <Group gap="xs" mt="xs" wrap="wrap">
                  {artist.artForms.map((a) => (
                    <Badge key={a.slug} variant="outline">
                      {a.name}
                    </Badge>
                  ))}
                </Group>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <MilestonesBlock kind="artist" data={milestones} />

        {artist.bio && (
          <section aria-labelledby="about-heading">
            <SectionTitle id="about-heading" title="About" order={3} />
            <Text style={{ whiteSpace: 'pre-wrap' }} maw={640} mt="xs">
              {artist.bio}
            </Text>
          </section>
        )}

        {artist.links.length > 0 && (
          <section aria-labelledby="links-heading">
            <SectionTitle id="links-heading" title="Links" order={3} />
            <Group gap="md" wrap="wrap" mt="xs">
              {artist.links.map((l, i) => (
                <Anchor
                  key={`${l.kind}-${i}`}
                  href={l.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  underline="hover"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  {l.label ?? linkLabel(l.kind)}
                  <ExternalLinkIcon />
                </Anchor>
              ))}
            </Group>
          </section>
        )}

        {artist.galleryImageUrls.length > 0 && (
          <section aria-labelledby="gallery-heading">
            <SectionTitle id="gallery-heading" title="Gallery" order={3} />
            <Group gap="xs" wrap="wrap" mt="xs">
              {artist.galleryImageUrls.map((url, i) => (
                <Image
                  key={i}
                  src={url}
                  alt={`Photo ${i + 1} of ${artist.displayName}`}
                  w={140}
                  h={100}
                  fit="cover"
                  radius="sm"
                  loading="lazy"
                  decoding="async"
                />
              ))}
            </Group>
          </section>
        )}

        <Divider />

        <section aria-labelledby="touring-heading">
          <Stack gap="md">
            <SectionTitle id="touring-heading" title="Touring history" />
            {artist.touringHistory.length === 0 ? (
              <EmptyState
                title="No stays on the road yet."
                body={
                  <>
                    Once {artist.displayName} completes their first exchange, the history lives here.
                  </>
                }
              />
            ) : (
              <Stack gap="xs">
                {artist.touringHistory.map((t) => (
                  <TouringRow key={t.exchangeId} entry={t} />
                ))}
              </Stack>
            )}
          </Stack>
        </section>

        <ArtistAvailabilitySection slug={artist.slug} />

        <Divider />

        <section aria-labelledby="reviews-heading">
          <Stack gap="md">
            <SectionTitle id="reviews-heading" title="Reviews from venues" />
            {artist.reviewSummary.visible && artist.reviewSummary.aggregateReviewScores ? (
              <AggregateScoresGrid scores={artist.reviewSummary.aggregateReviewScores} />
            ) : (
              <Text c="dimmed">
                Reviews appear after {artist.reviewSummary.threshold} completed exchange
                {artist.reviewSummary.threshold === 1 ? '' : 's'}.
              </Text>
            )}
            {artist.reviews.length > 0 || artist.reviewSummary.visible ? (
              <ReviewsSection
                reviews={artist.reviews}
                reviewsTotal={artist.reviews_total}
                viewerUserId={user?.id ?? null}
                revieweeUserId={artist.reviewee_user_id}
              />
            ) : null}
          </Stack>
        </section>
    </Stack>
  );
}

type ArtistAvailabilityDTO =
  | { visible: false }
  | {
      visible: true;
      defaultState: 'available' | 'unavailable';
      entries: CalendarEntry[];
      monthRange: { from: string; to: string };
    };

/**
 * Read-only availability calendar. The endpoint gates on the artist's chosen
 * visibility (public / venues-only / private) and returns `{ visible: false }`
 * when the viewer isn't allowed to see it — in which case this renders nothing,
 * including its leading divider.
 */
function ArtistAvailabilitySection({ slug }: { slug: string }): React.JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['artist-availability', slug],
    queryFn: async (): Promise<ArtistAvailabilityDTO> => {
      const res = await fetch(
        `/api/artist-calendar/by-slug/${encodeURIComponent(slug)}/availability`,
        { credentials: 'include' },
      );
      if (!res.ok) return { visible: false };
      return (await res.json()) as ArtistAvailabilityDTO;
    },
  });

  if (!data || !data.visible) return null;

  return (
    <>
      <Divider />
      <section aria-labelledby="availability-heading">
        <Stack gap="md">
          <SectionTitle id="availability-heading" title="Availability" />
          <AvailabilityCalendar
            entries={data.entries}
            exchanges={[]}
            defaultState={data.defaultState}
            readOnly
          />
        </Stack>
      </section>
    </>
  );
}

function TouringRow({ entry }: { entry: TouringHistoryEntry }): React.JSX.Element {
  return (
    <Card withBorder padding="md" radius="md">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Link
            to="/venues/$slug"
            params={{ slug: entry.venueSlug }}
            style={{
              color: 'var(--mantine-color-brand-7)',
              fontWeight: 600,
              fontSize: 'var(--mantine-font-size-md)',
              textDecoration: 'none',
            }}
          >
            {entry.venueName}
          </Link>
          <Text size="sm" c="dimmed">
            {entry.venueCity}
          </Text>
        </Stack>
        <Text size="sm" c="dimmed">
          {formatDate(entry.endDate)}
        </Text>
      </Group>
    </Card>
  );
}

function formatDate(iso: string): string {
  // YYYY-MM-DD → "5 Mar 2026". Tolerant of bad input.
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function NotFound(): React.JSX.Element {
  return (
    <Paper p="xl" withBorder>
      <Stack gap="xs" align="center">
        <Title order={3}>This profile isn&apos;t public.</Title>
        <Text c="dimmed" ta="center">
          The artist may not have published their profile yet, or the link may be old.
        </Text>
        <Button component={Link} to="/browse/artists" variant="default">
          Browse artists
        </Button>
      </Stack>
    </Paper>
  );
}

function linkLabel(kind: string): string {
  switch (kind) {
    case 'website':
      return 'Website';
    case 'instagram':
      return 'Instagram';
    case 'spotify':
      return 'Spotify';
    case 'bandcamp':
      return 'Bandcamp';
    case 'soundcloud':
      return 'SoundCloud';
    case 'mixcloud':
      return 'Mixcloud';
    case 'youtube':
      return 'YouTube';
    case 'vimeo':
      return 'Vimeo';
    case 'tiktok':
      return 'TikTok';
    case 'facebook':
      return 'Facebook';
    case 'apple_music':
      return 'Apple Music';
    case 'x':
      return 'X';
    default:
      return 'Link';
  }
}

function weightedAverage(scores: AggregateScores): number {
  const buckets = Object.values(scores).filter(
    (b): b is AggregateBucket => !!b && typeof b.avg === 'number',
  );
  if (buckets.length === 0) return 0;
  const totalCount = buckets.reduce((s, b) => s + b.count, 0);
  if (totalCount === 0) return 0;
  return buckets.reduce((s, b) => s + b.avg * b.count, 0) / totalCount;
}

function AggregateScoresGrid({ scores }: { scores: AggregateScores }): React.JSX.Element {
  const rows: { key: keyof AggregateScores; label: string }[] = [
    { key: 'communication', label: 'Communication' },
    { key: 'hospitality', label: 'Hospitality' },
    { key: 'performance', label: 'What they brought' },
    { key: 'safety', label: 'Safety' },
    { key: 'exchangeRespected', label: 'Exchange respected' },
  ];
  const visible = rows.filter((r) => {
    const bucket = scores[r.key];
    return bucket && bucket.count > 0;
  });
  if (visible.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        No category breakdown yet.
      </Text>
    );
  }
  return (
    <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 5 }} spacing="sm">
      {visible.map((r) => {
        const bucket = scores[r.key];
        if (!bucket) return null;
        return <DimensionCard key={r.key} label={r.label} bucket={bucket} />;
      })}
    </SimpleGrid>
  );
}

function DimensionCard({
  label,
  bucket,
}: {
  label: string;
  bucket: AggregateBucket;
}): React.JSX.Element {
  const filled = Math.max(0, Math.min(5, Math.round(bucket.avg)));
  return (
    <Card withBorder padding="sm" radius="md">
      <Stack gap={6}>
        <Text size="xs" c="dimmed" fw={500}>
          {label}
        </Text>
        <Group gap={6} align="baseline" wrap="nowrap">
          <Text size="lg" fw={700}>
            {bucket.avg.toFixed(1)}
          </Text>
          <Text size="xs" c="dimmed">
            ({bucket.count})
          </Text>
        </Group>
        <StarMeter filled={filled} />
      </Stack>
    </Card>
  );
}

function ExternalLinkIcon() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0, opacity: 0.6 }}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function StarMeter({ filled }: { filled: number }): React.JSX.Element {
  const stars = [0, 1, 2, 3, 4];
  return (
    <Box aria-label={`${filled} of 5`} style={{ display: 'flex', gap: 2, lineHeight: 1 }}>
      {stars.map((i) => (
        <Text
          key={i}
          component="span"
          size="sm"
          c={i < filled ? 'brand.6' : 'gray.4'}
          aria-hidden
        >
          ★
        </Text>
      ))}
    </Box>
  );
}
